import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useImageCache } from './hooks/useImageCache';

const ImageRenderer = React.memo(({ filename, alt = 'Generated image' }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getCachedImage, fetchAndCacheImage } = useImageCache();
  
  // Track if we've successfully loaded this filename to prevent re-loading
  const loadedFilenameRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // If we've already loaded this exact filename successfully, don't reload
    if (loadedFilenameRef.current === filename && imageUrl) {
      return;
    }

    const loadImage = async () => {
      if (!filename) {
        if (isMountedRef.current) {
          setError('No filename provided');
          setLoading(false);
        }
        return;
      }

      // Check cache first - if cached, don't show loading state
      const cached = getCachedImage(filename);
      if (cached) {
        if (isMountedRef.current) {
          setImageUrl(cached.url);
          setLoading(false);
          setError(null);
          loadedFilenameRef.current = filename;
        }
        return;
      }

      // Only show loading if we don't have the image yet
      if (!imageUrl && isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const cacheEntry = await fetchAndCacheImage(filename);
        if (isMountedRef.current) {
          setImageUrl(cacheEntry.url);
          setLoading(false);
          loadedFilenameRef.current = filename;
        }
      } catch (err) {
        console.error('ImageRenderer: Failed to fetch image:', filename, err);
        if (isMountedRef.current) {
          setError('Failed to load image');
          setLoading(false);
        }
      }
    };

    loadImage();
    // CRITICAL: Only depend on filename. Don't include cache functions to prevent re-triggering during streaming
  }, [filename]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: 100,
          p: 2,
          bgcolor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'divider'
        }}
      >
        <CircularProgress size={24} sx={{ mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Loading image...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: 100,
          p: 2,
          bgcolor: 'rgba(255, 0, 0, 0.05)',
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'error.main'
        }}
      >
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!imageUrl) {
    return null;
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      style={{
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'block'
      }}
      onError={() => {
        setError('Failed to display image');
        setImageUrl(null);
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Return true if props are equal (should NOT re-render)
  // Return false if props are different (should re-render)
  
  // Only re-render if filename or alt text changes
  return prevProps.filename === nextProps.filename && prevProps.alt === nextProps.alt;
});

export default ImageRenderer;
